import React, { useState, memo } from 'react';
import { IdeaCard, IdeaCardProps } from './IdeaCard';
import { HookCard, HookCardProps } from './HookCard';
import { ConceptCard, ConceptCardProps } from './ConceptCard';
import { ConceptDetails, ConceptDetailsData, SwipefileReference } from './ConceptDetails';
import { NewIdeaModal } from './NewIdeaModal';
import { Button } from './ui/Button';
import { Plus, Search, Filter } from 'lucide-react';
import { Input } from './ui/Input';
import { cn } from '../lib/utils';
type BrainTab = 'ideas' | 'hooks' | 'concepts';
// Mock Data - Banking/einfachbank focused
const MOCK_IDEAS: IdeaCardProps[] = [{
  id: '1',
  title: 'Credit Score Myths Debunked',
  description: 'Create a series debunking common credit score myths. Use whiteboard format to explain complex concepts in simple terms for Gen Z audience.',
  category: 'Financial Education',
  tags: ['Gen Z', 'Educational', 'Whiteboard'],
  dateAdded: 'Jan 15, 2024',
  status: 'new'
}, {
  id: '2',
  title: 'First Salary? Do This First',
  description: 'Talking head series targeting young professionals getting their first paycheck. Cover budgeting basics, emergency funds, and smart banking habits.',
  category: 'Young Professionals',
  tags: ['Practical', 'Beginner-Friendly', 'Talking Head'],
  dateAdded: 'Jan 14, 2024',
  status: 'new'
}, {
  id: '3',
  title: 'Banking Fees Explained',
  description: 'Professional deep-dive into banking fees and how to avoid them. Serious tone for main einfachbank account, targeting financially savvy customers.',
  category: 'Banking Basics',
  tags: ['Professional', 'Educational', 'Transparency'],
  dateAdded: 'Jan 12, 2024',
  status: 'new'
}];
const MOCK_HOOKS: HookCardProps[] = [{
  id: '1',
  hookText: "Your credit score is lying to you. Here's what banks actually look at when deciding if you're trustworthy...",
  sourceIdea: 'Credit Score Myths Debunked',
  category: 'Financial Education',
  tone: 'Bold',
  dateGenerated: 'Jan 16, 2024'
}, {
  id: '2',
  hookText: "Just got your first paycheck? Don't make the same mistake I did. Here's what you need to do in the first 24 hours...",
  sourceIdea: 'First Salary? Do This First',
  category: 'Young Professionals',
  tone: 'Conversational',
  dateGenerated: 'Jan 16, 2024'
}, {
  id: '3',
  hookText: "Banks charge you fees you don't even know about. I'll show you exactly where your money is going and how to stop it.",
  sourceIdea: 'Banking Fees Explained',
  category: 'Banking Basics',
  tone: 'Direct',
  dateGenerated: 'Jan 15, 2024'
}];
const MOCK_CONCEPTS: ConceptCardProps[] = [{
  id: '1',
  title: 'The Credit Score Truth',
  description: 'Whiteboard explainer breaking down credit score myths with hand-drawn visuals. Fast-paced, Gen Z-friendly approach with relatable examples.',
  thumbnail: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&auto=format&fit=crop&q=60',
  sourceHook: 'Your credit score is lying to you...',
  category: 'Financial Education',
  format: 'Whiteboard',
  status: 'ready',
  dateCreated: 'Jan 17, 2024'
}, {
  id: '2',
  title: 'First Paycheck Playbook',
  description: 'Talking head format with direct-to-camera advice. Professional yet approachable tone, targeting young professionals with actionable steps.',
  thumbnail: 'https://images.unsplash.com/photo-1633158829585-23ba8f7c8caf?w=800&auto=format&fit=crop&q=60',
  sourceHook: 'Just got your first paycheck...',
  category: 'Young Professionals',
  format: 'Talking Head',
  status: 'draft',
  dateCreated: 'Jan 16, 2024'
}, {
  id: '3',
  title: 'Hidden Banking Fees Exposed',
  description: 'Professional sketch-based breakdown of banking fees. Clean visuals with annotations showing exactly where fees appear and how to avoid them.',
  thumbnail: 'https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=800&auto=format&fit=crop&q=60',
  sourceHook: "Banks charge you fees you don't even know about...",
  category: 'Banking Basics',
  format: 'Sketch',
  status: 'published',
  dateCreated: 'Jan 18, 2024'
}];
// Mock detailed concept data - Banking focused
const MOCK_CONCEPT_DETAILS: ConceptDetailsData = {
  id: '1',
  title: 'The Credit Score Truth',
  thumbnail: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&auto=format&fit=crop&q=60',
  category: 'Financial Education',
  format: 'Whiteboard',
  status: 'ready',
  dateCreated: 'Jan 17, 2024',
  originalIdea: {
    title: 'Credit Score Myths Debunked',
    description: 'Create a series debunking common credit score myths. Use whiteboard format to explain complex concepts in simple terms for Gen Z audience.',
    tags: ['Gen Z', 'Educational', 'Whiteboard']
  },
  swipefileReferences: [{
    id: 'ref1',
    title: 'How Compound Interest Actually Works',
    thumbnail: 'https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=800&auto=format&fit=crop&q=60',
    topic: 'Financial Education',
    usedFor: ['Educational pacing', 'Complex concepts simplified', 'Visual explanation style']
  }, {
    id: 'ref2',
    title: '5 Banking Mistakes Gen Z Makes',
    thumbnail: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&auto=format&fit=crop&q=60',
    topic: 'Gen Z Banking',
    usedFor: ['Hook structure', 'Gen Z tone', 'Relatable examples']
  }, {
    id: 'ref3',
    title: 'Understanding Your Credit Score',
    thumbnail: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&auto=format&fit=crop&q=60',
    topic: 'Credit & Loans',
    usedFor: ['Topic authority', 'Information structure', 'Trust-building approach']
  }, {
    id: 'ref4',
    title: "What Banks Don't Want You to Know",
    thumbnail: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=800&auto=format&fit=crop&q=60',
    topic: 'Banking Secrets',
    usedFor: ['Bold hook style', 'Curiosity gap', 'Controversial framing']
  }],
  selectedHook: {
    text: "Your credit score is lying to you. Here's what banks actually look at when deciding if you're trustworthy...",
    tone: 'Bold'
  },
  script: {
    intro: "You think your credit score is the only thing banks care about? Wrong. I've worked in banking for 5 years, and I'm about to show you what really matters when you apply for a loan or credit card. This might actually save you thousands.",
    body: ["Myth #1: A perfect 850 credit score is what you need. Reality? Anything above 740 gets you the same rates. Banks look at your debt-to-income ratio way more than that extra 100 points. If you're making 50K but have 40K in debt, that 850 score won't help you.", "Myth #2: Checking your credit hurts your score. Nope. Soft inquiries don't touch your score. Hard inquiries from loan applications? Those matter, but only for about 10% of your score. Payment history is 35% - that's what really counts.", "Here's what banks actually care about: Can you pay them back? They want to see stable income, low debt, and a history of on-time payments. Your credit score is just one piece of a much bigger puzzle. Focus on the fundamentals, not the perfect number."],
    outro: "So stop obsessing over that perfect score. Pay your bills on time, keep your debt low, and you'll be fine. Want to learn more about what banks really look for? Follow for more banking truth bombs. And if this helped, drop a comment - I read every single one.",
    duration: '1:45'
  },
  visualReference: {
    style: 'Clean whiteboard animation style with hand-drawn elements. Casual, Gen Z-friendly aesthetic with bold colors and simple illustrations. Fast-paced editing with smooth transitions between concepts.',
    colorPalette: ['#139191', '#2D3748', '#F56565', '#48BB78', '#ECC94B'],
    shots: [{
      timestamp: '0:00-0:10',
      description: 'Opening: Hand draws credit score number with big X through it',
      notes: 'Bold, attention-grabbing visual. Use red X animation',
      inspiredBy: "What Banks Don't Want You to Know"
    }, {
      timestamp: '0:10-0:30',
      description: 'Whiteboard: Draw bank building with thought bubble showing what they actually look at',
      notes: 'Simple line drawings, quick reveals',
      inspiredBy: 'How Compound Interest Actually Works'
    }, {
      timestamp: '0:30-0:50',
      description: 'Myth #1: Draw scale showing 850 vs 740, then debt-to-income ratio',
      notes: 'Visual comparison, use contrasting colors',
      inspiredBy: 'Understanding Your Credit Score'
    }, {
      timestamp: '0:50-1:10',
      description: 'Myth #2: Draw soft vs hard inquiry icons, show percentage breakdown',
      notes: 'Pie chart animation for score factors',
      inspiredBy: 'How Compound Interest Actually Works'
    }, {
      timestamp: '1:10-1:35',
      description: 'Reality check: Draw three pillars - income, debt, payment history',
      notes: 'Clean, structured visual hierarchy',
      inspiredBy: '5 Banking Mistakes Gen Z Makes'
    }, {
      timestamp: '1:35-1:45',
      description: 'Closing: Hand writes key takeaway with checkmark',
      notes: 'Simple, memorable final frame',
      inspiredBy: "What Banks Don't Want You to Know"
    }]
  }
};
export function Brain() {
  const [activeTab, setActiveTab] = useState<BrainTab>('ideas');
  const [selectedConcept, setSelectedConcept] = useState<string | null>(null);
  const [isNewIdeaModalOpen, setIsNewIdeaModalOpen] = useState(false);
  const handleGenerateHooks = (ideaId: string) => {
    console.log('Generate hooks for idea:', ideaId);
    // TODO: Implement hook generation logic
  };
  const handleCreateConcept = (hookId: string) => {
    console.log('Create concept from hook:', hookId);
    // TODO: Implement concept creation logic
  };
  const handleViewConceptDetails = (conceptId: string) => {
    setSelectedConcept(conceptId);
  };
  const handleBackToConcepts = () => {
    setSelectedConcept(null);
  };
  const handleNewIdea = (idea: {
    title: string;
    description: string;
    references: any[];
  }) => {
    console.log('New idea created:', idea);
    // TODO: Add idea to list
  };
  // If viewing concept details, show that instead
  if (selectedConcept) {
    return <ConceptDetails concept={MOCK_CONCEPT_DETAILS} onBack={handleBackToConcepts} data-id="element-153" />;
  }
  const tabs = [{
    id: 'ideas' as BrainTab,
    label: 'Ideas',
    count: MOCK_IDEAS.length
  }, {
    id: 'hooks' as BrainTab,
    label: 'Hooks',
    count: MOCK_HOOKS.length
  }, {
    id: 'concepts' as BrainTab,
    label: 'Concepts',
    count: MOCK_CONCEPTS.length
  }];
  return <>
      <div className="flex-1 h-full flex flex-col bg-gray-50/50" data-id="element-154">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white/80 backdrop-blur-md border-b px-8 py-6" data-id="element-155">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6" data-id="element-156">
            <div data-id="element-157">
              <h1 className="text-3xl font-bold text-gray-900" data-id="element-158">Brain</h1>
              <p className="text-gray-500 mt-1" data-id="element-159">
                Transform ideas into hooks, and hooks into video concepts
              </p>
            </div>
            <Button className="bg-[#139191] hover:bg-[#139191]/90 text-white shadow-md gap-2" onClick={() => setIsNewIdeaModalOpen(true)} data-id="element-160">
              <Plus className="h-4 w-4" data-id="element-161" />
              New Idea
            </Button>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1 mb-4 border-b border-gray-200" data-id="element-162">
            {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={cn('px-4 py-2.5 text-sm font-medium transition-colors relative', activeTab === tab.id ? 'text-[#139191]' : 'text-gray-600 hover:text-gray-900')} data-id="element-163">
                {tab.label}
                <span className={cn('ml-2 px-2 py-0.5 rounded-full text-xs', activeTab === tab.id ? 'bg-[#139191]/10 text-[#139191]' : 'bg-gray-100 text-gray-600')} data-id="element-164">
                  {tab.count}
                </span>
                {activeTab === tab.id && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#139191]" data-id="element-165" />}
              </button>)}
          </div>

          {/* Search & Filter */}
          <div className="flex flex-col md:flex-row gap-4 items-center" data-id="element-166">
            <div className="relative flex-1 w-full" data-id="element-167">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" data-id="element-168" />
              <Input placeholder={`Search ${activeTab}...`} className="pl-10 bg-white border-gray-200 focus-visible:ring-[#139191]" data-id="element-169" />
            </div>
            <Button variant="outline" className="w-full md:w-auto gap-2 border-gray-200 text-gray-600 hover:text-[#139191] hover:border-[#139191]/30" data-id="element-170">
              <Filter className="h-4 w-4" data-id="element-171" />
              Filter
            </Button>
          </div>
        </div>

        {/* Content Grid */}
        <div className="flex-1 overflow-auto p-8" data-id="element-172">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 pb-8" data-id="element-173">
            {activeTab === 'ideas' && MOCK_IDEAS.map(idea => <IdeaCard key={idea.id} {...idea} onGenerateHooks={handleGenerateHooks} data-id="element-174" />)}
            {activeTab === 'hooks' && MOCK_HOOKS.map(hook => <HookCard key={hook.id} {...hook} onCreateConcept={handleCreateConcept} data-id="element-175" />)}
            {activeTab === 'concepts' && MOCK_CONCEPTS.map(concept => <ConceptCard key={concept.id} {...concept} onViewDetails={handleViewConceptDetails} data-id="element-176" />)}
          </div>
        </div>
      </div>

      {/* New Idea Modal */}
      <NewIdeaModal isOpen={isNewIdeaModalOpen} onClose={() => setIsNewIdeaModalOpen(false)} onSubmit={handleNewIdea} data-id="element-177" />
    </>;
}